import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicLayout from './public/PublicLayout';
import SEO from './common/SEO';
import { seoConfig } from '../config/seoConfig';
import {
  FiBook,
  FiAward,
  FiClock,
  FiStar,
  FiUsers,
  FiDollarSign,
  FiCheckCircle,
  FiChevronRight,
  FiX
} from 'react-icons/fi';

const VirtualUniversityPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [userProgress, setUserProgress] = useState([]);

  useEffect(() => {
    fetchCourses();
    checkMemberStatus();
    if (user) {
      fetchEnrollments();
      fetchUserProgress();
    }
  }, [user]);

  const checkMemberStatus = () => {
    // Check if user is authenticated and is a member
    const token = localStorage.getItem('userToken') || localStorage.getItem('token');
    setIsMember(!!token && !!user);
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        console.error('❌ REACT_APP_API_URL not configured!');
        setLoading(false);
        return;
      }
      console.log('==== FETCHING COURSES FOR VIRTUAL UNIVERSITY ====');
      const response = await fetch(`${API_BASE}/university/courses`);
      const data = await response.json();

      console.log('API Response:', data);
      console.log('All courses received:', data.data?.length);

      // Log each course
      if (data.data) {
        data.data.forEach((course, index) => {
          console.log(`VU Course ${index + 1}:`, {
            id: course.id,
            title: course.title,
            status: course.status,
            price: course.price,
            thumbnail_url: course.thumbnail_url,
            has_image: !!course.thumbnail_url
          });
        });
      }

      if (data.success) {
        // Show published, ongoing, and not_started courses
        const availableCourses = data.data.filter(course =>
          ['published', 'ongoing', 'not_started'].includes(course.status)
        );
        console.log('Filtered available courses:', availableCourses.length);
        console.log('Courses with images:', availableCourses.filter(c => c.thumbnail_url).length);

        // Log courses without images
        const coursesWithoutImages = availableCourses.filter(c => !c.thumbnail_url);
        if (coursesWithoutImages.length > 0) {
          console.warn('Courses missing images:', coursesWithoutImages.map(c => c.title));
        }

        setCourses(availableCourses);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      if (!token) return;
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        console.error('❌ REACT_APP_API_URL not configured!');
        return;
      }

      const response = await fetch(`${API_BASE}/university/my-enrollments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setEnrolledCourses(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
    }
  };

  const fetchUserProgress = async () => {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      if (!token) return;
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        console.error('❌ REACT_APP_API_URL not configured!');
        return;
      }

      const response = await fetch(`${API_BASE}/university/my-progress`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setUserProgress(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const categories = [
    'all',
    'respiratory_care',
    'anatomy_physiology',
    'pathophysiology',
    'medical_devices',
    'patient_care',
    'ethics_law',
    'research_methods',
    'pharmacology',
    'clinical_skills',
    'emergency_care'
  ];

  const categoryLabels = {
    'all': 'All Categories',
    'respiratory_care': 'Respiratory Care',
    'anatomy_physiology': 'Anatomy & Physiology',
    'pathophysiology': 'Pathophysiology',
    'medical_devices': 'Medical Devices',
    'patient_care': 'Patient Care',
    'ethics_law': 'Ethics & Law',
    'research_methods': 'Research Methods',
    'pharmacology': 'Pharmacology',
    'clinical_skills': 'Clinical Skills',
    'emergency_care': 'Emergency Care'
  };

  const levels = ['all', 'beginner', 'intermediate', 'advanced', 'expert'];

  const levelLabels = {
    'all': 'All Levels',
    'beginner': 'Beginner',
    'intermediate': 'Intermediate',
    'advanced': 'Advanced',
    'expert': 'Expert'
  };

  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || course.level === selectedLevel;
    return matchesCategory && matchesLevel;
  });

  const isEnrolled = (courseId) => {
    return enrolledCourses.some(enrollment => enrollment.course_id === courseId);
  };

  const getCourseProgress = (courseId) => {
    return userProgress.find(progress => progress.course_id === courseId);
  };

  const handleViewDetails = (course) => {
    console.log('View Details clicked for:', course.title);
    setSelectedCourse(course);
    setShowCourseDetails(true);
  };

  const handleEnrollFromDetails = () => {
    if (!selectedCourse) return;

    // Check if user is logged in first
    if (!isMember) {
      setShowCourseDetails(false);
      toast.warning('Please log in to enroll in this course. Redirecting to login page...', {
        position: 'top-center',
        autoClose: 3000
      });
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      return;
    }

    setShowCourseDetails(false);

    if (isEnrolled(selectedCourse.id)) {
      toast.info('You are already enrolled in this course.', {
        position: 'top-center',
        autoClose: 3000
      });
      return;
    }

    setShowEnrollmentForm(true);
  };

  const handleEnrollNow = (course) => {
    console.log('Enroll Now clicked for:', course.title, 'Price:', course.price, 'Is Free:', !course.price || course.price === 0);

    // Check if user is logged in first
    if (!isMember) {
      toast.warning('Please log in to enroll in this course. Redirecting to login page...', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      return;
    }

    if (isEnrolled(course.id)) {
      toast.info('You are already enrolled in this course. Check your progress dashboard.', {
        position: 'top-center',
        autoClose: 3000
      });
      return;
    }

    setSelectedCourse(course);
    setShowEnrollmentForm(true);
  };

  const CourseCard = ({ course }) => {
    const enrolled = isEnrolled(course.id);
    const progress = getCourseProgress(course.id);
    const isFree = !course.price || course.price === 0;
    const finalPrice = course.discount_percentage > 0
      ? (course.price * (1 - course.discount_percentage / 100)).toFixed(2)
      : course.price;

    // Determine if user can access this course
    const canEnroll = isFree ? isMember : true; // Free courses = members only, Paid courses = anyone

    const getLevelBadgeClass = (level) => {
      switch (level) {
        case 'beginner': return 'bg-success bg-opacity-10 text-success';
        case 'intermediate': return 'bg-warning bg-opacity-10 text-warning';
        case 'advanced': return 'bg-danger bg-opacity-10 text-danger';
        case 'expert': return 'bg-info bg-opacity-10 text-info';
        default: return 'bg-secondary bg-opacity-10 text-secondary';
      }
    };

    const getStatusBadgeClass = (status) => {
      switch (status) {
        case 'ongoing': return 'bg-success bg-opacity-10 text-success';
        case 'completed': return 'bg-secondary bg-opacity-10 text-secondary';
        default: return 'bg-primary bg-opacity-10 text-primary';
      }
    };

    return (
      <div className="card h-100 shadow-sm border-0 overflow-hidden" style={{ transition: 'transform 0.3s, box-shadow 0.3s' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px)';
          e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0,0,0,0.075)';
        }}>
        <div className="position-relative">
          <img
            src={course.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${course.thumbnail_url}` : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="250"%3E%3Crect fill="%23e9ecef" width="400" height="250"/%3E%3Ctext fill="%236c757d" font-family="Arial" font-size="20" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E'}
            alt={course.title}
            className="card-img-top"
            style={{ height: '200px', objectFit: 'cover', backgroundColor: '#e9ecef' }}
            onError={(e) => {
              console.error('Failed to load course image:', course.title, course.thumbnail_url);
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="250"%3E%3Crect fill="%23e9ecef" width="400" height="250"/%3E%3Ctext fill="%236c757d" font-family="Arial" font-size="16" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage Not Found%3C/text%3E%3C/svg%3E';
            }}
            onLoad={() => {
              if (course.thumbnail_url) {
                console.log('✅ Image loaded successfully:', course.title);
              }
            }}
          />
          <div className="position-absolute top-0 start-0 m-2">
            <span className={`badge ${getLevelBadgeClass(course.level)} fw-normal`}>
              {course.level ? course.level.charAt(0).toUpperCase() + course.level.slice(1) : 'N/A'}
            </span>
          </div>
          <div className="position-absolute top-0 end-0 m-2">
            {isFree ? (
              <div className="d-flex flex-column align-items-end gap-1">
                <span className="badge bg-success fw-normal">FREE</span>
                <span className="badge bg-warning text-dark fw-normal" style={{ fontSize: '0.7rem' }}>Members Only</span>
              </div>
            ) : course.discount_percentage > 0 ? (
              <div className="d-flex flex-column align-items-end gap-1">
                <span className="badge bg-danger fw-normal">-{course.discount_percentage}%</span>
                <span className="badge bg-dark fw-normal">${finalPrice}</span>
              </div>
            ) : (
              <span className="badge bg-dark fw-normal">${course.price}</span>
            )}
          </div>
          {enrolled && (
            <div className="position-absolute bottom-0 start-0 end-0 m-2">
              <div className="bg-white bg-opacity-90 rounded p-2">
                <div className="d-flex justify-content-between align-items-center small">
                  <span className="fw-medium">Progress:</span>
                  <span className="fw-bold">{progress?.progress_percentage || 0}%</span>
                </div>
                <div className="progress mt-1" style={{ height: '6px' }}>
                  <div
                    className="progress-bar"
                    style={{ width: `${progress?.progress_percentage || 0}%`, backgroundColor: '#6B8E23' }}
                    role="progressbar"
                    aria-valuenow={progress?.progress_percentage || 0}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <span className="badge" style={{ backgroundColor: '#6B8E23', fontSize: '0.75rem' }}>
              {categoryLabels[course.category] || course.category}
            </span>
            {course.status && (
              <span className={`badge ${getStatusBadgeClass(course.status)} fw-normal`}>
                {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
              </span>
            )}
          </div>

          <h5 className="card-title fw-bold mb-2">{course.title}</h5>
          <p className="card-text text-muted small mb-3" style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {course.short_description || course.description}
          </p>

          <div className="d-flex justify-content-between align-items-center text-muted small mb-3">
            <div className="d-flex align-items-center gap-1">
              <FiClock size={14} />
              <span>{course.duration}h</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <FiUsers size={14} />
              <span>{course.enrollment_count || 0}</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <FiAward size={14} />
              <span>{course.cpd_points || 0} CPD</span>
            </div>
          </div>

          <hr />

          <div className="d-flex justify-content-between align-items-center gap-2">
            <div className="d-flex flex-column">
              <small className="text-muted mb-1">
                {course.instructor ? `by ${course.instructor}` : 'ARCA Instructor'}
              </small>
              {!isFree && course.price > 0 && (
                <div className="d-flex align-items-center gap-1">
                  <FiDollarSign size={16} style={{ color: '#6B8E23' }} />
                  <span className="fw-bold" style={{ color: '#6B8E23', fontSize: '1.1rem' }}>
                    ${parseFloat(course.price).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
            <div className="d-flex gap-1">
              <button
                onClick={() => handleViewDetails(course)}
                className="btn btn-sm btn-outline-success"
                style={{ borderColor: '#6B8E23', color: '#6B8E23' }}
              >
                DETAILS
              </button>
              {enrolled ? (
                <button
                  onClick={() => setShowProgress(true)}
                  className="btn btn-sm text-white"
                  style={{ backgroundColor: '#6B8E23' }}
                >
                  PROGRESS
                </button>
              ) : (
                <button
                  onClick={() => handleEnrollNow(course)}
                  className="btn btn-sm text-white"
                  style={{
                    backgroundColor: canEnroll ? '#6B8E23' : '#6c757d',
                    border: 'none'
                  }}
                  disabled={!canEnroll}
                  title={!canEnroll ? 'Free courses are for members only. Paid courses are open to all.' : ''}
                >
                  {isFree && !isMember ? 'MEMBERS ONLY' : 'ENROLL'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <PublicLayout>
        <SEO
          title={seoConfig.virtualUniversity.title}
          description={seoConfig.virtualUniversity.description}
          keywords={seoConfig.virtualUniversity.keywords}
          url={seoConfig.virtualUniversity.url}
        />
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO
        title={seoConfig.virtualUniversity.title}
        description={seoConfig.virtualUniversity.description}
        keywords={seoConfig.virtualUniversity.keywords}
        url={seoConfig.virtualUniversity.url}
      />
      <div className="bg-light py-4">
        {/* Header */}
        <div className="bg-white shadow-sm mb-4">
          <div className="container py-5">
            <div className="text-center">
              <h1 className="display-5 fw-bold mb-3" style={{ color: '#6B8E23' }}>
                ARCA Virtual University
              </h1>
              <p className="lead text-muted mb-0">
                Advance your respiratory therapy career with our comprehensive online courses designed by industry experts
              </p>
            </div>
          </div>
        </div>

        <div className="container">
          {/* Filters */}
          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label fw-medium">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="form-select"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {categoryLabels[category] || category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label fw-medium">Level</label>
                  <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="form-select"
                  >
                    {levels.map(level => (
                      <option key={level} value={level}>
                        {levelLabels[level] || level}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Course Grid */}
          <div className="row g-4">
            {filteredCourses.map(course => (
              <div key={course.id} className="col-12 col-md-6 col-lg-4">
                <CourseCard course={course} />
              </div>
            ))}
          </div>

          {filteredCourses.length === 0 && (
            <div className="text-center py-5">
              <FiBook size={48} className="text-muted mb-3" />
              <h3 className="fw-medium text-dark mb-2">No courses found</h3>
              <p className="text-muted">Try adjusting your filters to see more courses.</p>
            </div>
          )}
        </div>
      </div>

      {/* Course Details Modal */}
      {showCourseDetails && selectedCourse && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ borderBottom: '2px solid #6B8E23' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#6B8E23' }}>
                  Course Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCourseDetails(false);
                    setSelectedCourse(null);
                  }}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {/* Course Image */}
                {selectedCourse.thumbnail_url && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedCourse.thumbnail_url}`}
                    alt={selectedCourse.title}
                    className="img-fluid rounded mb-4"
                    style={{ width: '100%', maxHeight: '300px', objectFit: 'cover' }}
                  />
                )}

                {/* Course Title & Price */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h3 className="fw-bold mb-2">{selectedCourse.title}</h3>
                    <p className="text-muted mb-0">
                      {selectedCourse.instructor ? `by ${selectedCourse.instructor}` : 'ARCA Instructor'}
                    </p>
                  </div>
                  <div className="text-end">
                    {!selectedCourse.price || selectedCourse.price === 0 ? (
                      <div>
                        <span className="badge bg-success fs-5 px-3 py-2">FREE</span>
                        <p className="text-muted small mb-0 mt-1">Members Only</p>
                      </div>
                    ) : (
                      <div>
                        <h3 className="fw-bold mb-0" style={{ color: '#6B8E23' }}>
                          ${parseFloat(selectedCourse.price).toFixed(2)}
                        </h3>
                        <p className="text-muted small mb-0">USD</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Course Stats */}
                <div className="row g-3 mb-4">
                  <div className="col-4">
                    <div className="text-center p-3 bg-light rounded">
                      <FiClock size={24} style={{ color: '#6B8E23' }} />
                      <p className="mb-0 mt-2 fw-medium">{selectedCourse.duration || 'N/A'}h</p>
                      <small className="text-muted">Duration</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="text-center p-3 bg-light rounded">
                      <FiAward size={24} style={{ color: '#6B8E23' }} />
                      <p className="mb-0 mt-2 fw-medium">{selectedCourse.cpd_points || 0}</p>
                      <small className="text-muted">CPD Points</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="text-center p-3 bg-light rounded">
                      <FiUsers size={24} style={{ color: '#6B8E23' }} />
                      <p className="mb-0 mt-2 fw-medium">{selectedCourse.enrollment_count || 0}</p>
                      <small className="text-muted">Students</small>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">About This Course</h5>
                  <p className="text-muted">
                    {selectedCourse.description || selectedCourse.short_description || 'No description available.'}
                  </p>
                </div>

                {/* Course Info */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="fw-bold text-muted small">CATEGORY</h6>
                      <p className="mb-0">{categoryLabels[selectedCourse.category] || selectedCourse.category || 'General'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="fw-bold text-muted small">LEVEL</h6>
                      <p className="mb-0">{selectedCourse.level ? selectedCourse.level.charAt(0).toUpperCase() + selectedCourse.level.slice(1) : 'All Levels'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="fw-bold text-muted small">LOCATION</h6>
                      <p className="mb-0">{selectedCourse.location_type === 'online' ? 'Online' : selectedCourse.location || 'Onsite'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="fw-bold text-muted small">STATUS</h6>
                      <p className="mb-0">{selectedCourse.status ? selectedCourse.status.charAt(0).toUpperCase() + selectedCourse.status.slice(1) : 'Available'}</p>
                    </div>
                  </div>
                </div>

                {/* Start & End Dates */}
                {(selectedCourse.start_date || selectedCourse.end_date) && (
                  <div className="mb-4">
                    <h5 className="fw-bold mb-3">Course Schedule</h5>
                    <div className="row">
                      {selectedCourse.start_date && (
                        <div className="col-md-6">
                          <h6 className="fw-bold text-muted small">START DATE</h6>
                          <p className="mb-0">{new Date(selectedCourse.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                      )}
                      {selectedCourse.end_date && (
                        <div className="col-md-6">
                          <h6 className="fw-bold text-muted small">END DATE</h6>
                          <p className="mb-0">{new Date(selectedCourse.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Login Required Notice for Non-Members */}
              {!isMember && (
                <div className="alert alert-info border-0 mx-4 mb-3" style={{
                  backgroundColor: '#e7f3ff',
                  borderLeft: '4px solid #6B8E23'
                }}>
                  <div className="d-flex align-items-start gap-2">
                    <FiCheckCircle size={20} style={{ color: '#6B8E23', marginTop: '2px' }} />
                    <div>
                      <p className="fw-bold mb-1" style={{ color: '#2C5234' }}>Login Required to Enroll</p>
                      <p className="small mb-0 text-muted">
                        You must be logged in as an ARCA member to complete course registration.
                        {(!selectedCourse.price || selectedCourse.price === 0) && (
                          <> Free courses are exclusively available to ARCA members only.</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6' }}>
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={() => {
                    setShowCourseDetails(false);
                    setSelectedCourse(null);
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn text-white px-4 d-flex align-items-center"
                  style={{
                    backgroundColor: '#6B8E23',
                    border: 'none',
                    fontWeight: '500'
                  }}
                  onClick={handleEnrollFromDetails}
                >
                  <FiCheckCircle className="me-2" size={18} />
                  {!isMember ? 'Login to Enroll' : 'Enroll Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Form Modal */}
      {showEnrollmentForm && (
        <CourseEnrollmentForm
          course={selectedCourse}
          onClose={() => {
            setShowEnrollmentForm(false);
            setSelectedCourse(null);
          }}
        />
      )}
    </PublicLayout>
  );
};

// Import the enrollment form component
const CourseEnrollmentForm = ({ course, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    country: '',
    city: '',
    educationBackground: '',
    profession: '',
    selectedCourse: course?.title || '',
    preferredLearningOption: 'Online'
  });

  const [showPayment, setShowPayment] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: ''
  });

  const [processing, setProcessing] = useState(false);
  const [enrollmentComplete, setEnrollmentComplete] = useState(false);

  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
    'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
    'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
    'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary',
    'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast',
    'Jamaica', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
    'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
    'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar',
    'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'São Tomé and Príncipe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen',
    'Zambia', 'Zimbabwe'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Check if course is free for members
    const isFree = !course.price || course.price === 0 || course.course_type === 'free';

    console.log('Course Enrollment Submission:');
    console.log('- Course:', course.title);
    console.log('- Price:', course.price);
    console.log('- Course Type:', course.course_type);
    console.log('- Is Free:', isFree);
    console.log('- User is Member:', isMember);

    if (isFree) {
      // Free courses for members - skip payment and enroll directly
      console.log('✅ Free course for member - enrolling directly without payment');
      processEnrollment();
    } else {
      // Paid course - redirect to course payment page with Stripe
      console.log('💳 Paid course - redirecting to payment page');
      navigate('/course-payment', {
        state: {
          courseId: course.id,
          courseName: course.title,
          amount: parseFloat(course.price),
          studentInfo: formData
        }
      });
      onClose(); // Close the enrollment form
    }
  };

  const processPayment = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Process enrollment after successful payment
      await processEnrollment();
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const processEnrollment = async () => {
    try {
      const isFree = !course.price || course.price === 0;
      const enrollmentData = {
        courseId: course.id,
        studentInfo: formData,
        paymentInfo: isFree ? null : paymentData,
        amount: course.price || 0,
        currency: 'USD'
      };

      // Check for various token types used in the app
      const token = localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken') ||
        localStorage.getItem('token'); // fallback for generic token

      console.log('Enrollment Debug Info:');
      console.log('- Course ID:', course.id);
      console.log('- Course Title:', course.title);
      console.log('- Course Price:', course.price);
      console.log('- Is Free:', isFree);
      console.log('- Token found:', token ? 'Yes' : 'No');
      console.log('- Student Info:', formData);

      if (!token) {
        toast.error('No authentication token found. Please log in again.');
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL;

      if (!API_BASE) {
        toast.error('System configuration error. Please contact support.');
        return;
      }

      const response = await fetch(`${API_BASE}/university/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(enrollmentData)
      });

      console.log('API Response Status:', response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Enrollment Success:', responseData);
        toast.success('Enrollment successful!');
        setEnrollmentComplete(true);
      } else if (response.status === 402) {
        // Payment required error
        const errorData = await response.json();
        console.error('Payment Required:', errorData);
        toast.error(errorData.message || 'Payment is required for this course');
        // Redirect to payment page
        navigate('/course-payment', {
          state: {
            courseId: course.id,
            courseName: course.title,
            amount: parseFloat(course.price),
            studentInfo: formData
          }
        });
        onClose();
      } else {
        const errorData = await response.text();
        console.error('Enrollment API Error:', errorData);
        toast.error(`Enrollment failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Enrollment failed:', error);
      toast.error(`Enrollment failed: ${error.message}. Please try again.`);
    }
  };

  if (enrollmentComplete) {
    return (
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-body text-center p-5" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="mb-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle p-4"
                  style={{
                    backgroundColor: '#e8f5e9',
                    width: '100px',
                    height: '100px'
                  }}>
                  <FiCheckCircle size={64} style={{ color: '#6B8E23' }} />
                </div>
              </div>
              <h3 className="h3 fw-bold mb-3" style={{ color: '#2C5234' }}>
                🎉 Enrollment Successful!
              </h3>
              <p className="text-muted mb-2" style={{ fontSize: '1.1rem' }}>
                You have successfully enrolled in
              </p>
              <p className="fw-bold mb-4" style={{ color: '#6B8E23', fontSize: '1.2rem' }}>
                "{course.title}"
              </p>
              <div className="alert alert-light border mb-4">
                <p className="small mb-0 text-muted">
                  📧 A confirmation email has been sent to your email address with course access details and next steps.
                </p>
              </div>
              <button
                onClick={onClose}
                className="btn text-white px-5 py-2"
                style={{
                  background: 'linear-gradient(135deg, #6B8E23 0%, #2C5234 100%)',
                  border: 'none',
                  fontWeight: '600'
                }}
              >
                ✓ Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-lg" style={{ maxHeight: '90vh' }}>
        <div className="modal-content border-0 shadow-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header" style={{
            background: 'linear-gradient(135deg, #6B8E23 0%, #2C5234 100%)',
            borderBottom: 'none',
            padding: '1.5rem'
          }}>
            <div>
              <h5 className="modal-title fw-bold text-white mb-1">
                {showPayment ? '💳 Payment Details' : '📚 Course Enrollment Application'}
              </h5>
              <p className="text-white-50 small mb-0">Complete the form below to register</p>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          {!showPayment ? (
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body" style={{ backgroundColor: '#f8f9fa', padding: '2rem', overflowY: 'auto', maxHeight: 'calc(90vh - 180px)' }}>
                {/* Course Information Card */}
                <div className="card border-0 shadow-sm mb-4" style={{
                  borderLeft: '4px solid #6B8E23',
                  background: 'linear-gradient(to right, #f0f8ff, #ffffff)'
                }}>
                  <div className="card-body">
                    <div className="d-flex align-items-start gap-3">
                      <div className="p-3 rounded-circle" style={{ backgroundColor: '#6B8E23' }}>
                        <FiBook size={24} className="text-white" />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-2" style={{ color: '#2C5234' }}>Selected Course</h6>
                        <h5 className="mb-2 fw-bold" style={{ color: '#6B8E23' }}>{course.title}</h5>
                        <div className="d-flex flex-wrap gap-3 small">
                          <span className="badge" style={{
                            backgroundColor: !course.price || course.price === 0 ? '#28a745' : '#6B8E23',
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem'
                          }}>
                            {!course.price || course.price === 0 ? '🎓 FREE Course' : `💵 $${parseFloat(course.price).toFixed(2)} USD`}
                          </span>
                          <span className="badge bg-secondary" style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
                            ⭐ {course.cpd_points || 0} CPD Points
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Free Course Notice for Members */}
                {(!course.price || course.price === 0 || course.course_type === 'free') && (
                  <div className="alert border-0 shadow-sm mb-4" style={{
                    backgroundColor: '#d4edda',
                    borderLeft: '4px solid #28a745'
                  }}>
                    <div className="d-flex align-items-start gap-2">
                      <span style={{ fontSize: '1.5rem' }}>🎉</span>
                      <div>
                        <h6 className="fw-bold mb-1" style={{ color: '#155724' }}>
                          🎓 FREE Course for ARCA Members
                        </h6>
                        <p className="small mb-0" style={{ color: '#155724' }}>
                          This course is completely FREE for ARCA members. No payment or card details required.
                          Simply complete the enrollment form below and you'll get instant access!
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Personal Information Section */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3" style={{ color: '#2C5234', borderBottom: '2px solid #6B8E23', paddingBottom: '0.5rem' }}>
                    👤 Personal Information
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label fw-medium">Title</label>
                      <select
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select</option>
                        <option>Mr.</option>
                        <option>Mrs.</option>
                        <option>Ms.</option>
                        <option>Dr.</option>
                        <option>Prof.</option>
                        <option>RRT.</option>
                        <option>RCP.</option>
                        <option>RT.</option>
                        <option>RN.</option>
                        <option>CRNA</option>
                        <option>PT/DPT</option>
                        <option>OT/OTD</option>
                        <option>SLP</option>
                        <option>PA/PA-C</option>
                        <option>NP/APRN</option>
                        <option>DDS/DMD</option>
                        <option>DVM</option>
                        <option>PharmD</option>
                        <option>OD</option>
                        <option>LCSW</option>
                        <option>LPN</option>
                        <option>LMT</option>
                        <option>RD/RDN</option>
                        <option>DO</option>
                      </select>
                    </div>

                    <div className="col-md-9">
                      <label className="form-label fw-medium">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Date of Birth *</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        required
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Gender *</label>
                      <select
                        name="gender"
                        required
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Country *</label>
                      <select
                        name="country"
                        required
                        value={formData.country}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">City *</label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Profession / Occupation *</label>
                      <input
                        type="text"
                        name="profession"
                        required
                        value={formData.profession}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-medium">Educational Background / Qualification *</label>
                      <textarea
                        name="educationBackground"
                        required
                        rows={3}
                        value={formData.educationBackground}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Please describe your educational background and relevant qualifications..."
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3" style={{ color: '#2C5234', borderBottom: '2px solid #6B8E23', paddingBottom: '0.5rem' }}>
                    📖 Learning Preferences
                  </h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-medium">Preferred Learning Option</label>
                      <input
                        type="text"
                        name="preferredLearningOption"
                        value={formData.preferredLearningOption}
                        readOnly
                        className="form-control bg-light"
                      />
                    </div>
                  </div>
                </div>

                {/* Important Notice */}
                <div className="alert alert-light border-start border-4" style={{ borderColor: '#6B8E23 !important', backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-items-start gap-2">
                    <FiCheckCircle size={20} style={{ color: '#6B8E23', marginTop: '2px' }} />
                    <div>
                      <p className="mb-1 fw-medium" style={{ color: '#2C5234' }}>Before you submit:</p>
                      <ul className="small mb-0 text-muted" style={{ paddingLeft: '1.2rem' }}>
                        <li>Ensure all information provided is accurate</li>
                        <li>You will receive a confirmation email after enrollment</li>
                        <li>Course access will be granted immediately upon approval</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-white" style={{
                borderTop: '2px solid #e9ecef',
                padding: '1.25rem 2rem',
                flexShrink: 0
              }}>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-outline-secondary px-4 py-2"
                  style={{ fontWeight: '500' }}
                >
                  ✕ Cancel
                </button>
                <button
                  type="submit"
                  className="btn text-white px-5 py-2 d-flex align-items-center shadow-sm"
                  style={{
                    background: 'linear-gradient(135deg, #6B8E23 0%, #2C5234 100%)',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  <FiCheckCircle className="me-2" size={20} />
                  {!course.price || course.price === 0 ? '✓ Complete Enrollment' : '➜ Proceed to Payment'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={processPayment}>
              <div className="modal-body" style={{ backgroundColor: '#f8f9fa', padding: '2rem', overflowY: 'auto', maxHeight: 'calc(90vh - 180px)' }}>
                {/* Order Summary Card */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: '4px solid #6B8E23' }}>
                  <div className="card-body">
                    <h6 className="fw-bold mb-3" style={{ color: '#2C5234' }}>Order Summary</h6>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">Course:</span>
                      <span className="fw-medium">{course.title}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                      <span className="text-muted">Amount:</span>
                      <span className="h4 fw-bold mb-0" style={{ color: '#6B8E23' }}>
                        ${parseFloat(course.price).toFixed(2)} USD
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold" style={{ color: '#2C5234' }}>Total:</span>
                      <span className="h3 fw-bold mb-0" style={{ color: '#6B8E23' }}>
                        ${parseFloat(course.price).toFixed(2)} USD
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Information Section */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3" style={{ color: '#2C5234', borderBottom: '2px solid #6B8E23', paddingBottom: '0.5rem' }}>
                    💳 Payment Information
                  </h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-medium">Card Number *</label>
                      <input
                        type="text"
                        name="cardNumber"
                        required
                        placeholder="1234 5678 9012 3456"
                        value={paymentData.cardNumber}
                        onChange={handlePaymentChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Expiry Date *</label>
                      <input
                        type="text"
                        name="expiryDate"
                        required
                        placeholder="MM/YY"
                        value={paymentData.expiryDate}
                        onChange={handlePaymentChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">CVV *</label>
                      <input
                        type="text"
                        name="cvv"
                        required
                        placeholder="123"
                        value={paymentData.cvv}
                        onChange={handlePaymentChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-medium">Name on Card *</label>
                      <input
                        type="text"
                        name="nameOnCard"
                        required
                        value={paymentData.nameOnCard}
                        onChange={handlePaymentChange}
                        className="form-control"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Notice */}
                <div className="alert alert-warning border-0" style={{ backgroundColor: '#fff3cd' }}>
                  <div className="d-flex align-items-start gap-2">
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                    <div>
                      <p className="fw-medium mb-1" style={{ color: '#856404' }}>Test Payment Environment</p>
                      <p className="small mb-0" style={{ color: '#856404' }}>
                        This is a test environment. You can use any card number for testing purposes. No actual charges will be made.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-white" style={{
                borderTop: '2px solid #e9ecef',
                padding: '1.25rem 2rem',
                flexShrink: 0
              }}>
                <button
                  type="button"
                  onClick={() => setShowPayment(false)}
                  className="btn btn-outline-secondary px-4 py-2"
                  disabled={processing}
                  style={{ fontWeight: '500' }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="btn text-white px-5 py-2 d-flex align-items-center shadow-sm"
                  style={{
                    background: processing ? '#6c757d' : 'linear-gradient(135deg, #6B8E23 0%, #2C5234 100%)',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiDollarSign className="me-2" size={20} />
                      Pay ${parseFloat(course.price).toFixed(2)}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualUniversityPage;